import { resolveBookingBreakdownDisplay, roundBookingCurrency, inferBookingTaxInclusive } from './bookingPriceCalculation';

export const round2 = roundBookingCurrency;

export const getBookingCurrencySymbol = (currency = 'INR') => {
  const code = String(currency || 'INR').toUpperCase();
  try {
    const parts = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(0);
    const currencyPart = parts.find((part) => part.type === 'currency');
    return currencyPart?.value || code;
  } catch {
    return code;
  }
};

export const formatBookingCurrency = (amount, currency = 'INR') => {
  const code = String(currency || 'INR').toUpperCase();
  if (amount == null || amount === '' || Number(amount) === 0) {
    return `${getBookingCurrencySymbol(code)}0.00`;
  }
  const value = round2(amount);
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

function sumBookingItems(bookingData) {
  const bookingItems = bookingData?.items || bookingData?.booking_items || [];
  if (!Array.isArray(bookingItems) || bookingItems.length === 0) return 0;
  return round2(
    bookingItems.reduce(
      (sum, item) =>
        sum +
        (Number(item?.price) || 0) +
        (Number(item?.taxes) || 0) +
        (Number(item?.fees) || 0),
      0
    )
  );
}

/**
 * Unified payment summary for all booking drawers, payment collection, and detail pages.
 * Reads API payment_summary when present; falls back to booking fields and item totals.
 */
export const getPaymentSummaryData = (bookingData, payments = []) => {
  const paymentSummary = bookingData?.payment_summary || {};
  const itemBasedSubtotal = sumBookingItems(bookingData);

  const paidAmount =
    paymentSummary?.total_paid !== undefined
      ? Number(paymentSummary.total_paid) || 0
      : Array.isArray(payments) && payments.length > 0
        ? payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
        : Number(
            bookingData?.advance_amount ||
              bookingData?.advance ||
              bookingData?.advance_paid ||
              0
          ) || 0;

  const subtotalBeforeDiscountRaw =
    paymentSummary?.subtotal_before_discount ??
    bookingData?.subtotal_before_discount ??
    (itemBasedSubtotal > 0
      ? itemBasedSubtotal
      : bookingData?.total_amount ?? bookingData?.total ?? 0);

  const discountAmountRaw =
    paymentSummary?.discount_amount ?? bookingData?.discount_amount ?? 0;
  const discountPercentage =
    paymentSummary?.discount_percentage ?? bookingData?.discount_percentage ?? 0;
  const subtotalAfterDiscountRaw =
    paymentSummary?.subtotal_after_discount ?? bookingData?.subtotal_after_discount;

  const subtotalBeforeDiscountNum = Number(subtotalBeforeDiscountRaw || 0);
  let discountAmountNum = Number(discountAmountRaw || 0);
  let subtotalAfterDiscountNum =
    subtotalAfterDiscountRaw != null ? Number(subtotalAfterDiscountRaw || 0) : NaN;
  const discountPercentageNum = Number(discountPercentage || 0);
  const gstAmountRaw = Number(paymentSummary?.gst_amount ?? bookingData?.gst_amount ?? 0) || 0;
  const gstPercentage = Number(paymentSummary?.gst_percentage ?? bookingData?.gst_percentage ?? 0) || 0;
  const gstIncluded = Boolean(
    paymentSummary?.gst_included ?? bookingData?.gst_included ?? false
  );
  const taxInclusiveFlag = Boolean(
    paymentSummary?.tax_inclusive ?? bookingData?.tax_inclusive ?? false
  );

  if (discountAmountNum <= 0 && discountPercentageNum > 0 && subtotalBeforeDiscountNum > 0) {
    discountAmountNum = (subtotalBeforeDiscountNum * discountPercentageNum) / 100;
  }
  if (!Number.isFinite(subtotalAfterDiscountNum)) {
    subtotalAfterDiscountNum = Math.max(0, subtotalBeforeDiscountNum - discountAmountNum);
  }
  // Tax-inclusive bookings store taxable (ex-GST) amount in subtotal_after_discount.
  // That gap is GST extraction, not a discount — only infer discount for exclusive GST.
  const beforeAfterGap = subtotalBeforeDiscountNum - subtotalAfterDiscountNum;
  const gapLooksLikeGstExtraction =
    gstIncluded
    && gstAmountRaw > 0
    && beforeAfterGap > 0
    && Math.abs(beforeAfterGap - gstAmountRaw) <= 0.05;
  if (
    discountAmountNum <= 0
    && discountPercentageNum <= 0
    && !gapLooksLikeGstExtraction
    && !(gstIncluded && taxInclusiveFlag)
    && beforeAfterGap > 0
  ) {
    discountAmountNum = beforeAfterGap;
  }
  const extraChargesAmountRaw = Number(
    paymentSummary?.extra_charges_amount ?? bookingData?.extra_charges_amount ?? 0
  ) || 0;
  const extraChargesBaseAmountRaw = Number(
    paymentSummary?.extra_charges_base_amount ?? bookingData?.extra_charges_base_amount ?? extraChargesAmountRaw
  ) || 0;
  const extraChargesGstAmountRaw = Number(
    paymentSummary?.extra_charges_gst_amount ?? bookingData?.extra_charges_gst_amount ?? 0
  ) || 0;
  const extraChargesNote =
    paymentSummary?.extra_charges_note ?? bookingData?.extra_charges_note ?? '';
  const extraChargesItems =
    paymentSummary?.extra_charges_items ?? bookingData?.extra_charges_items ?? [];

  const subtotalBeforeDiscount = round2(subtotalBeforeDiscountNum);
  const discountAmount = round2(discountAmountNum);
  const subtotalAfterDiscount = round2(
    Math.max(
      0,
      Number.isFinite(subtotalAfterDiscountNum)
        ? subtotalAfterDiscountNum
        : subtotalBeforeDiscountNum - discountAmountNum
    )
  );

  const persistedTotal = Number(
    paymentSummary?.total_amount ?? bookingData?.total_amount ?? bookingData?.total ?? 0
  );
  let totalAmount = persistedTotal;
  if (totalAmount <= 0) {
    totalAmount =
      gstIncluded && taxInclusiveFlag
        ? round2(subtotalBeforeDiscount - discountAmount)
        : round2(subtotalAfterDiscount + gstAmountRaw);
  }

  const taxInclusive = inferBookingTaxInclusive({
    gstIncluded,
    taxInclusive: taxInclusiveFlag,
    subtotalBeforeDiscount,
    discountAmount,
    totalAmount,
    extraChargesAmount: extraChargesAmountRaw,
  });

  let gstAmount = round2(gstAmountRaw);
  let normalizedSubtotalAfterDiscount = subtotalAfterDiscount;
  if (taxInclusive && gstIncluded && gstPercentage > 0) {
    const grossAfterDiscount = round2(subtotalBeforeDiscount - discountAmount);
    const roomTotalOnly = round2(Math.max(0, totalAmount - extraChargesAmountRaw));
    // Compare against room portion of total (exclude extras) so inclusive GST still extracts correctly.
    if (
      grossAfterDiscount > 0
      && (
        Math.abs(roomTotalOnly - grossAfterDiscount) <= 0.05
        || Math.abs(totalAmount - grossAfterDiscount) <= 0.05
      )
    ) {
      const extractedGst = round2(grossAfterDiscount * gstPercentage / (100 + gstPercentage));
      gstAmount = extractedGst;
      normalizedSubtotalAfterDiscount = round2(grossAfterDiscount - extractedGst);
      // Keep persisted total when extras are present; otherwise room gross is the total.
      if (extraChargesAmountRaw <= 0) {
        totalAmount = grossAfterDiscount;
      }
    }
  }

  const remainingAmount =
    paymentSummary?.remaining_balance !== undefined
      ? Number(paymentSummary.remaining_balance) || 0
      : bookingData?.remaining_amount !== undefined && bookingData?.remaining_amount !== null
        ? Number(bookingData.remaining_amount) || 0
        : totalAmount - paidAmount;

  return resolveBookingBreakdownDisplay({
    subtotalBeforeDiscount,
    discountAmount,
    subtotalAfterDiscount: normalizedSubtotalAfterDiscount,
    gstAmount,
    gstPercentage,
    gstIncluded,
    taxInclusive,
    totalAmount,
    discountPercentage,
    paidAmount,
    remainingAmount,
    currency: bookingData?.currency || 'INR',
    extraChargesAmount: round2(extraChargesAmountRaw),
    extraChargesBaseAmount: round2(extraChargesBaseAmountRaw),
    extraChargesGstAmount: round2(extraChargesGstAmountRaw),
    extraChargesNote,
    extraChargesItems,
  });
};

/** Normalize restaurant_orders_summary from booking detail APIs. */
export const parseRestaurantOrdersSummary = (bookingData) => {
  const payload = bookingData?.restaurant_orders_summary;
  if (!payload?.has_orders) {
    return { hasOrders: false, summary: {}, orders: [] };
  }
  return {
    hasOrders: true,
    summary: payload.summary || {},
    orders: Array.isArray(payload.orders) ? payload.orders : [],
  };
};

/**
 * Attach restaurant + combined totals onto a hotel payment summary.
 * Used by booking details / check-in / check-out / add-payment drawers.
 */
export const enrichPaymentSummaryWithRestaurant = (hotelSummary, bookingData) => {
  if (!hotelSummary) return null;
  const restaurant = parseRestaurantOrdersSummary(bookingData);
  const currency = hotelSummary.currency || bookingData?.currency || 'INR';
  const hotelTotal = round2(hotelSummary.displayTotal ?? hotelSummary.totalAmount ?? 0);
  const hotelPaid = round2(hotelSummary.paidAmount ?? 0);
  const hotelRemaining = round2(hotelSummary.remainingAmount ?? Math.max(0, hotelTotal - hotelPaid));

  if (!restaurant.hasOrders) {
    return {
      ...hotelSummary,
      hasRestaurant: false,
      hotelTotal,
      hotelPaid,
      hotelRemaining,
      restaurantTotal: 0,
      restaurantPaid: 0,
      restaurantRemaining: 0,
      restaurantSubtotal: 0,
      restaurantTax: 0,
      restaurantDiscount: 0,
      combinedTotal: hotelTotal,
      combinedPaid: hotelPaid,
      combinedRemaining: hotelRemaining,
      currency,
    };
  }

  const s = restaurant.summary || {};
  const restaurantTotal = round2(s.total_amount || 0);
  const restaurantPaid = round2(s.paid_amount || 0);
  const restaurantRemaining = round2(
    s.remaining_amount != null
      ? s.remaining_amount
      : Math.max(0, restaurantTotal - restaurantPaid),
  );
  const restaurantSubtotal = round2(s.subtotal || 0);
  const restaurantTax = round2(s.tax_amount || 0);
  const restaurantDiscount = round2(s.discount_amount || 0);
  const restaurantAdditional = round2(s.additional_charges || 0);

  return {
    ...hotelSummary,
    hasRestaurant: true,
    hotelTotal,
    hotelPaid,
    hotelRemaining,
    restaurantTotal,
    restaurantPaid,
    restaurantRemaining,
    restaurantSubtotal,
    restaurantTax,
    restaurantDiscount,
    restaurantAdditional,
    combinedTotal: round2(hotelTotal + restaurantTotal),
    combinedPaid: round2(hotelPaid + restaurantPaid),
    combinedRemaining: round2(Math.max(0, hotelRemaining + restaurantRemaining)),
    currency,
  };
};

/** Convenience: hotel summary + restaurant enrichment in one call. */
export const getEnrichedPaymentSummaryData = (bookingData, payments = []) =>
  enrichPaymentSummaryWithRestaurant(getPaymentSummaryData(bookingData, payments), bookingData);

/**
 * True when hotel due is 0, and restaurant due is 0 when restaurant orders exist.
 */
export const isBookingPaymentClear = (summary) => {
  if (!summary) return false;
  const hotelDue = Number(
    summary.hotelRemaining ?? summary.remainingAmount ?? 0
  );
  if (hotelDue > 0.009) return false;
  if (summary.hasRestaurant) {
    return Number(summary.restaurantRemaining ?? 0) <= 0.009;
  }
  return true;
};

export const parsePaymentsApiResponse = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.Data)) return data.Data;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.payments)) return data.payments;
  return [];
};
